import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Wallet, DollarSign, TrendingUp, ArrowRight, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawalsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [methods, setMethods] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [formData, setFormData] = useState({
    amount: '',
    method: '',
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    paypal_email: ''
  });

  useEffect(() => {
    loadWithdrawalData();
  }, []);

  const loadWithdrawalData = async () => {
    try {
      const [userRes, methodsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/withdrawals/methods`),
        axios.get(`${BACKEND_URL}/api/withdrawals`, { withCredentials: true })
      ]);
      
      setUser(userRes.data);
      setMethods(methodsRes.data.methods);
      setWithdrawals(withdrawalsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Load withdrawal data error:', error);
      setLoading(false);
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const amount = parseFloat(formData.amount);
      
      if (!amount || amount <= 0) {
        toast.error('Please enter a valid amount');
        setProcessing(false);
        return;
      }

      if (amount > user.total_savings) {
        toast.error(`Insufficient balance. Available: $${user.total_savings.toFixed(2)}`);
        setProcessing(false);
        return;
      }

      let accountDetails = {};
      
      if (formData.method === 'stripe_bank') {
        accountDetails = {
          account_holder_name: formData.account_holder_name,
          routing_number: formData.routing_number,
          account_number: formData.account_number
        };
      } else if (formData.method === 'paypal') {
        accountDetails = {
          paypal_email: formData.paypal_email
        };
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/withdrawals/request`,
        {
          amount: amount,
          currency: 'USD',
          method: formData.method,
          account_details: accountDetails
        },
        { withCredentials: true }
      );

      if (formData.method === 'keep_balance') {
        toast.success('Your balance will continue to grow with interest!');
      } else {
        toast.success(response.data.message);
      }
      
      setFormData({
        amount: '',
        method: '',
        account_holder_name: '',
        routing_number: '',
        account_number: '',
        paypal_email: ''
      });
      
      loadWithdrawalData();
    } catch (error) {
      console.error('Withdrawal error:', error);
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setProcessing(false);
    }
  };

  const selectedMethod = methods.find(m => m.id === formData.method);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-stone-900 mb-2">Withdrawals</h1>
          <p className="text-stone-600">Withdraw your savings or keep your balance growing</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card data-testid="available-balance-card" className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-stone-600">Available Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-stone-900">
                  ${user?.total_savings?.toFixed(2) || '0.00'}
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-stone-600">Total Withdrawn</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-stone-900">
                  ${withdrawals.reduce((sum, w) => w.status === 'completed' ? sum + w.amount : sum, 0).toFixed(2)}
                </div>
                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-stone-600">Pending Withdrawals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-amber-500">
                  ${withdrawals.reduce((sum, w) => w.status === 'pending' ? sum + w.amount : sum, 0).toFixed(2)}
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle>Request Withdrawal</CardTitle>
              <CardDescription>Choose how you'd like to receive your savings</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleWithdrawal} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Withdrawal Amount</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3 w-5 h-5 text-stone-400" />
                    <Input
                      data-testid="withdrawal-amount-input"
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={user?.total_savings || 0}
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className="pl-10 rounded-xl"
                      required
                    />
                  </div>
                  <p className="text-sm text-stone-500">
                    Available: ${user?.total_savings?.toFixed(2) || '0.00'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="method">Withdrawal Method</Label>
                  <Select
                    value={formData.method}
                    onValueChange={(value) => setFormData({ ...formData, method: value })}
                    required
                  >
                    <SelectTrigger data-testid="withdrawal-method-select" className="rounded-xl">
                      <SelectValue placeholder="Select withdrawal method" />
                    </SelectTrigger>
                    <SelectContent>
                      {methods.map((method) => (
                        <SelectItem key={method.id} value={method.id}>
                          {method.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedMethod && (
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <div className="text-sm text-stone-600 space-y-1">
                      <div><strong>Processing Time:</strong> {selectedMethod.processing_time}</div>
                      <div><strong>Fee:</strong> {selectedMethod.fee}</div>
                      <div className="text-xs text-stone-500 mt-2">{selectedMethod.description}</div>
                    </div>
                  </div>
                )}

                {formData.method === 'stripe_bank' && (
                  <div className="space-y-4 p-4 border border-stone-200 rounded-xl">
                    <div className="space-y-2">
                      <Label htmlFor="account_holder_name">Account Holder Name</Label>
                      <Input
                        data-testid="account-holder-input"
                        id="account_holder_name"
                        value={formData.account_holder_name}
                        onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                        placeholder="John Doe"
                        className="rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="routing_number">Routing Number</Label>
                      <Input
                        data-testid="routing-number-input"
                        id="routing_number"
                        value={formData.routing_number}
                        onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                        placeholder="110000000"
                        className="rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="account_number">Account Number</Label>
                      <Input
                        data-testid="account-number-input"
                        id="account_number"
                        value={formData.account_number}
                        onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                        placeholder="000123456789"
                        className="rounded-xl"
                        required
                      />
                    </div>
                  </div>
                )}

                {formData.method === 'paypal' && (
                  <div className="space-y-2 p-4 border border-stone-200 rounded-xl">
                    <Label htmlFor="paypal_email">PayPal Email</Label>
                    <Input
                      data-testid="paypal-email-input"
                      id="paypal_email"
                      type="email"
                      value={formData.paypal_email}
                      onChange={(e) => setFormData({ ...formData, paypal_email: e.target.value })}
                      placeholder="you@example.com"
                      className="rounded-xl"
                      required
                    />
                  </div>
                )}

                {formData.method === 'keep_balance' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-green-900">Keep Balance Growing</div>
                        <div className="text-xs text-green-700 mt-1">
                          Your balance will continue to earn interest and grow over time!
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  data-testid="submit-withdrawal-button"
                  type="submit"
                  disabled={processing || !formData.method}
                  className="w-full rounded-xl py-6 bg-indigo-600 hover:bg-indigo-700"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Request Withdrawal
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle>Withdrawal History</CardTitle>
            </CardHeader>
            <CardContent>
              {withdrawals.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-600">No withdrawals yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {withdrawals.map((withdrawal) => (
                    <div
                      key={withdrawal.withdrawal_id}
                      data-testid={`withdrawal-${withdrawal.withdrawal_id}`}
                      className="p-4 border border-stone-200 rounded-xl"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-stone-900">
                          ${withdrawal.amount.toFixed(2)}
                        </div>
                        <Badge
                          className={
                            withdrawal.status === 'completed'
                              ? 'bg-green-100 text-green-700 hover:bg-green-100'
                              : withdrawal.status === 'pending'
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                              : 'bg-red-100 text-red-700 hover:bg-red-100'
                          }
                        >
                          {withdrawal.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {withdrawal.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {withdrawal.status === 'failed' && <AlertCircle className="w-3 h-3 mr-1" />}
                          {withdrawal.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-stone-600">
                        <div>Method: {withdrawal.method.replace('_', ' ')}</div>
                        <div>
                          Date: {new Date(withdrawal.created_at).toLocaleDateString()}
                        </div>
                        {withdrawal.transaction_id && (
                          <div className="text-xs text-stone-500 mt-1">
                            ID: {withdrawal.transaction_id}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
